import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, ListCell, NameCell } from '@/components/TableCells'
import type { TeamGroup } from '@/services/users/teamGroupsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  type: 'type',
  objectState: 'objectState',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface TeamGroupTableProps {
  teamGroups: TeamGroup[]
  loading?: boolean
  emptyMessage?: string
  selection?: TeamGroup[]
  onSelectionChange?: (teamGroups: TeamGroup[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function TeamGroupTable({
  teamGroups,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: TeamGroupTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<TeamGroup, unknown>[] = [
    ...(selectable ? [selectionColumn<TeamGroup>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {row.original.description || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'type',
      header: t('common.type'),
      meta: { minWidth: 140 },
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {row.original.type ? t(`teams.types.${row.original.type}`, row.original.type) : '—'}
        </span>
      ),
    },
    {
      id: 'teams',
      header: t('teamGroups.teams'),
      enableSorting: false,
      size: 90,
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">{row.original.teams.length}</span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<TeamGroup>({
      id: 'labels',
      header: t('common.labels'),
      enableSorting: false,
      meta: { minWidth: 160 },
      cell: ({ row }) => <ListCell values={row.original.labels} />,
    }),
    optionalColumn<TeamGroup>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<TeamGroup>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={teamGroups}
      loading={loading}
      emptyMessage={emptyMessage ?? t('teamGroups.noTeamGroupsFound')}
      columnsKey="teamGroups"
      onRowClick={(row) => navigate(`/admin/team-groups/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
